/**
 * Privacy Policy Page
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPolicy = () => {
  const lastUpdated = 'December 5, 2025';

  return (
    <div className="legal-container">
      <div className="legal-card">
        <Link to="/login" className="legal-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Login
        </Link>

        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: {lastUpdated}</p>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              FusionICT ("we," "our," or "us") respects your privacy and is committed to protecting your personal data.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
              our billing and invoicing platform ("Service").
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>Name and contact information (email address, phone number, business address)</li>
              <li>Account credentials (email and encrypted password)</li>
              <li>Billing information (payment method details, transaction history)</li>
              <li>Business information (company name, tax identification numbers)</li>
              <li>Usage data (login times, features accessed, IP addresses)</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you access our Service, we automatically collect:</p>
            <ul>
              <li>Device information (browser type, operating system)</li>
              <li>Log data (access times, pages viewed, referring URLs)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>To provide and maintain our Service</li>
              <li>To process transactions and send related information</li>
              <li>To send administrative notifications and updates</li>
              <li>To respond to inquiries and provide customer support</li>
              <li>To detect, prevent, and address technical issues or fraud</li>
              <li>To comply with legal obligations</li>
              <li>To improve and personalize user experience</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> With third-party vendors who assist in providing our Service (payment processors, cloud hosting providers)</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal data, including:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure cloud infrastructure (Google Cloud Platform)</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure,
              and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal data for as long as necessary to fulfill the purposes outlined in this Privacy Policy,
              unless a longer retention period is required or permitted by law. Financial records may be retained for up to
              7 years for tax and accounting purposes.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify inaccurate or incomplete data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p>To exercise these rights, please contact us at privacy@fusionict.com.</p>
          </section>

          <section>
            <h2>8. Cookies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience. You can control cookies
              through your browser settings. Essential cookies are required for the Service to function properly.
            </p>
          </section>

          <section>
            <h2>9. Third-Party Services</h2>
            <p>Our Service integrates with third-party services including:</p>
            <ul>
              <li>Google Cloud Platform (infrastructure)</li>
              <li>Firebase (authentication and database)</li>
              <li>Stripe and PayMongo (payment processing)</li>
            </ul>
            <p>These services have their own privacy policies governing the use of your information.</p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect
              personal information from children. If you believe we have collected information from a child,
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2>11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence.
              We ensure appropriate safeguards are in place to protect your data in accordance with applicable laws.
            </p>
          </section>

          <section>
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting
              the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the
              Service after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <div className="contact-info">
              <p><strong>FusionICT</strong></p>
              <p>Email: privacy@fusionict.com</p>
              <p>Address: Manila, Philippines</p>
            </div>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/terms">Terms of Service</Link>
          <span className="separator">•</span>
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
